import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  PaperProps,
  Tab,
  TextField,
  Tooltip,
} from '@mui/material';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { searchConfiguration } from '../../api/ConfigurationApi';
import AddIcon from '@mui/icons-material/Add';
import HomeIcon from '@mui/icons-material/Home';
import InfoIcon from '@mui/icons-material/Info';
import { useNavigate } from 'react-router';
import { DetailsPageStyle } from '../../../style/Styles';
import Draggable from 'react-draggable';
import CloseIcon from '@mui/icons-material/Close';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import ProductImageGallery from '../Layouts/ProductImageGallery';
import {
  addConfiguration,
  resetConfiguration,
  setSearchValue,
} from '../../store/slice/ConfigurationSlice';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from 'store';
import { IConfigurationDetail } from '../../../types';
import { getValueById } from '../../services/DetailsPageHelper';
import { showToastMessage } from '../../services/ToastHelper';
import { EMessageType, ETabValue } from '../../data/Constants';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

function PaperComponent(props: PaperProps) {
  return (
    <Draggable handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper {...props} />
    </Draggable>
  );
}

export default function ActionPanel() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [tabValue, setTabValue] = useState<string | null>();

  const configuration = useSelector((state: AppState) => state.configuration);

  const { productId, searchValue, shortSalesText } = configuration;
  const productName = getValueById(shortSalesText, productId)?.text;

  const configurationSearchRef = useRef<HTMLInputElement>(null);

  //ImageDialog
  const [open, setOpen] = useState(false);

  const handleImageDialogClickOpen = () => {
    setTabValue(ETabValue.SalesText);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleChange = (...params: [React.SyntheticEvent, string]) => {
    setTabValue(params[1]);
  };

  const handleSearchConfiguration = async () => {
    const value = configurationSearchRef.current?.value?.trim();
    //check if value is empty
    if (value === '') {
      showToastMessage(EMessageType.Error, t('message.error.invalidConfiguration'), false);
      return;
    }

    //check if config exists
    const hasConfiguration = configuration.configurations.some(
      (configuration: IConfigurationDetail) => configuration.configurationId === value
    );

    if (hasConfiguration) {
      showToastMessage(EMessageType.Info, t('message.configurationExists'), false);
      return;
    }

    dispatch(setSearchValue(value));
    const response = await searchConfiguration(value);
    if (response?.data) {
      dispatch(addConfiguration(response?.data));
    }
  };

  const gotoHome = () => {
    dispatch(resetConfiguration());
    dispatch(setSearchValue(''));
    navigate('/cva');
  };

  const handleSearch = (e) => {
    dispatch(setSearchValue(e.target.value));
  };

  const clearSearch = () => {
    dispatch(setSearchValue(''));
  };

  return (
    <Grid container sx={{ width: '100%', flexDirection: 'column' }}>
      <Grid xs={12} sm={12} md={12} lg={12} xl={12}>
        <Box sx={DetailsPageStyle.productName}>
          <Tooltip title={t('tooltip.home')}>
            <Box display="inline-flex" alignItems="center">
              <IconButton
                data-testid="home-button"
                sx={{ p: '0.8rem', color: 'inherit' }}
                aria-label="home"
                disabled={!configuration.configurationId}
                onClick={gotoHome}
              >
                <HomeIcon />
              </IconButton>
            </Box>
          </Tooltip>
          {configuration.configurationId ? (
            <Box className="d-flex align-center">
              {productId + ' (' + productName + ')'}
              <Tooltip title={t('tooltip.productInfo')}>
                <Box display="inline-flex" alignItems="center">
                  <IconButton
                    data-testid="info-icon"
                    sx={{ p: '0.8rem' }}
                    aria-label="productInfo"
                    onClick={handleImageDialogClickOpen}
                  >
                    <InfoIcon style={{ color: '#1474A4' }} />
                  </IconButton>
                </Box>
              </Tooltip>
            </Box>
          ) : (
            <></>
          )}
        </Box>
        <Box className="actionBarFlex">
          {configuration.configurationId ? (
            <TextField
              key="feature-search"
              data-testid="feature-search-box"
              variant="standard"
              sx={{ m: 1, width: 250, display: 'flex', marginRight: 4 }}
              onChange={(e) => handleSearch(e)}
              placeholder={t('button.search')}
              value={searchValue ?? ''}
              slotProps={{
                input: {
                  startAdornment: <SearchIcon />,
                  endAdornment: (
                    <IconButton
                      data-testid="feature-search-icon"
                      title="Clear"
                      aria-label="Clear"
                      size="small"
                      style={{ visibility: searchValue ? 'visible' : 'hidden' }}
                      onClick={clearSearch}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  ),
                },
              }}
            />
          ) : (
            <TextField
              key="configuration-search"
              inputRef={configurationSearchRef}
              data-testid="configurationId-search-box"
              sx={{ m: 1, width: 400, display: 'flex' }}
              placeholder={t('label.configurationId')}
              type="text"
              variant="standard"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSearchConfiguration();
                }
              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        data-testid="configurationId-add-icon"
                        aria-label="search"
                        onClick={() => handleSearchConfiguration()}
                      >
                        <AddIcon className="addIcon" />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}

          <Dialog
            className="common-dialog-style infoDialogStyle"
            open={open}
            onClose={handleClose}
            slotProps={{ paper: { className: 'paper' } }}
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
            data-testid="productInfoDialogBox"
          >
            <DialogTitle className="header title">
              <div className="infoDialogTitle">{productName + ' (' + productId + ')'}</div>
            </DialogTitle>
            <DialogContent>
              <IconButton
                data-testid="info-close-button"
                className="closeIcon"
                aria-label="close"
                onClick={handleClose}
              >
                <CloseIcon />
              </IconButton>
              <TabContext value={tabValue ?? ETabValue.SalesText}>
                <Box className="dialog-box">
                  <TabList onChange={handleChange}>
                    <Tab
                      className="dialog-tab"
                      value={ETabValue.SalesText}
                      label={t('label.salesText')}
                    />
                    <Tab
                      className="dialog-tab"
                      value={ETabValue.ProductImages}
                      label={t('label.productImages')}
                    />
                  </TabList>
                </Box>
                <TabPanel className="dialog-panel" value={ETabValue.ProductImages}>
                  <ProductImageGallery />
                </TabPanel>
              </TabContext>
            </DialogContent>
          </Dialog>
        </Box>
      </Grid>
    </Grid>
  );
}
